// Product image helper — renders <img> for real URLs OR an elegant placeholder
// card for markers like "placeholder:Front view". This lets us launch with
// a scalable product schema and swap in real photos later without code changes.
import { useState } from "react";

export const isPlaceholder = (src) =>
  typeof src === "string" && src.startsWith("placeholder:");

export const getPlaceholderLabel = (src) =>
  isPlaceholder(src) ? src.replace("placeholder:", "") : "";

const PlaceholderCard = ({ label, className = "", small = false }) => (
  <div
    data-testid="product-image-placeholder"
    className={`relative w-full h-full bg-zinc-950 flex items-center justify-center overflow-hidden ${className}`}
  >
    {/* Grid backdrop */}
    <div
      aria-hidden
      className="absolute inset-0 opacity-40"
      style={{
        backgroundImage:
          "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }}
    />
    {/* Yellow brush motif */}
    <svg
      viewBox="0 0 40 200"
      className="absolute left-6 top-1/2 -translate-y-1/2 h-3/5"
      aria-hidden
    >
      <path
        d="M8,6 L34,3 L36,196 L4,194 L2,120 L8,55 Z"
        fill="#FFCC00"
        opacity="0.85"
      />
    </svg>
    {/* Corner ticks */}
    <span className="absolute top-3 left-3 w-3 h-3 border-t border-l border-yellow-400/40" />
    <span className="absolute top-3 right-3 w-3 h-3 border-t border-r border-yellow-400/40" />
    <span className="absolute bottom-3 left-3 w-3 h-3 border-b border-l border-yellow-400/40" />
    <span className="absolute bottom-3 right-3 w-3 h-3 border-b border-r border-yellow-400/40" />
    {/* Label */}
    <div className="relative z-10 text-center px-6">
      <p
        className={`overline mb-3 ${small ? "text-[9px]" : ""}`}
        style={{ letterSpacing: "0.4em" }}
      >
        CABO UNSTOPPABLE
      </p>
      <p
        className="font-display text-white uppercase leading-none"
        style={{
          fontSize: small ? "1rem" : "clamp(1.25rem, 3vw, 2.5rem)",
        }}
      >
        {label}
      </p>
      {!small && (
        <p className="mt-4 text-[10px] uppercase tracking-[0.35em] text-white/40">
          Photo · Coming Soon
        </p>
      )}
    </div>
  </div>
);

export const ProductImg = ({ src, alt = "", label, className = "", small = false, ...rest }) => {
  const [errored, setErrored] = useState(false);
  if (isPlaceholder(src) || errored) {
    const finalLabel = label || getPlaceholderLabel(src) || "Cabo Unstoppable";
    return <PlaceholderCard label={finalLabel} className={className} small={small} />;
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setErrored(true)}
      {...rest}
    />
  );
};

export default ProductImg;
